import {
  createEnvelopeFromTemplate,
  discardEnvelope,
  reconcileEnvelope,
  useEsignTaskPrefill,
  useSignatures,
  type SignatureEnvelope,
} from '@commenda/api/docusign';
import { FeatureLaunch, isFeatureLaunched } from '@commenda/feature-launches';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Input,
  Label,
  showNotification,
} from '@commenda/kakashi';
import {
  EsignStepMode,
  THumanEsignStepDefinition,
  THumanEsignTemplateStepDefinition,
} from '@commenda/types';
import {
  SignatureEnvelopeStatus,
  SignatureRecipientStatus,
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useServiceRequestViewerCapabilities } from '../../../context/ServiceRequestViewerCapabilities';
import { SignatureTaskCard } from '../../../docusign/SignatureTaskCard';
import { ServiceRequestTaskCard } from '../../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../../types';
import { ServiceRequestTaskLabel } from '../../labels';
import { ServiceRequestTaskIconConfig } from '../../taskIconConfig';
import { AdHocSignatureTaskModal } from './AdHocSignatureTaskModal';

type TEsignPhase =
  | 'select'
  | 'draft'
  | 'in_flight'
  | 'action_required'
  | 'done';

type TSignerDraft = { name: string; email: string };

const ACTIVE_TASK_STATUSES: TaskStatus[] = [
  TaskStatus.TO_DO,
  TaskStatus.IN_PROGRESS,
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * HUMAN_ESIGN workflow task. Phase is derived from the task's envelope
 * history (no local state machine):
 * - select: no envelope yet → assignee resolves signer roles and sends
 * - in_flight: latest envelope SENT/DELIVERED → per-signer status + remind/void
 * - action_required: latest envelope DECLINED/VOIDED → restart with a fresh envelope
 * - done: latest envelope COMPLETED → download signed document
 */
export const WorkflowEsignTask = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { workflowExecution, role, serviceRequest, mutate } = context;
  const [sendModalOpen, setSendModalOpen] = useState(false);
  // False everywhere but the enterprise matter page for an affiliate admin.
  const { canRequestSignature } = useServiceRequestViewerCapabilities();

  const stepDefinition = useMemo(() => {
    if (!workflowExecution?.WorkflowTemplateVersion?.definition) return null;
    return workflowExecution.WorkflowTemplateVersion.definition.steps.find(
      (step) => step.id === task.workflowDefinitionStepId
    ) as THumanEsignStepDefinition | undefined;
  }, [workflowExecution, task.workflowDefinitionStepId]);

  const [hasInFlightEnvelope, setHasInFlightEnvelope] = useState(false);
  const isAdHoc = stepDefinition?.mode === EsignStepMode.AD_HOC;
  // A definition carrying an ad-hoc step can be promoted into an environment the feature has not
  // reached. The backend would accept the envelope, so the send flow is what gets withheld.
  const isAdHocSendable =
    isAdHoc && isFeatureLaunched(FeatureLaunch.ESIGN_AD_HOC_DOCUMENT);

  const { envelopes, mutate: mutateEnvelopes } = useSignatures(
    { serviceRequestId: serviceRequest?.id },
    { refreshInterval: hasInFlightEnvelope ? 7000 : 0 }
  );

  const taskEnvelopes = useMemo(
    () =>
      (envelopes ?? [])
        .filter((envelope) => envelope.serviceRequestTaskId === task.id)
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        ),
    [envelopes, task.id]
  );

  const latestEnvelope: SignatureEnvelope | undefined = taskEnvelopes[0];

  useEffect(() => {
    const alreadyInFlightStatuses = [
      SignatureEnvelopeStatus.SENT,
      SignatureEnvelopeStatus.DELIVERED,
      SignatureEnvelopeStatus.CREATED,
    ];

    setHasInFlightEnvelope(
      latestEnvelope != null &&
        alreadyInFlightStatuses.some(
          (status) => latestEnvelope.status === status
        )
    );
  }, [latestEnvelope]);

  useEffect(() => {
    void mutateEnvelopes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.status, task.updatedAt]);

  const reconciledEnvelopeIds = useRef(new Set<string>());

  useEffect(() => {
    if (latestEnvelope?.status !== SignatureEnvelopeStatus.CREATED) return;
    if (reconciledEnvelopeIds.current.has(latestEnvelope.id)) return;

    reconciledEnvelopeIds.current.add(latestEnvelope.id);
    reconcileEnvelope(latestEnvelope.id)
      .then(() => mutateEnvelopes())
      .catch(() => undefined);
  }, [latestEnvelope?.id, latestEnvelope?.status, mutateEnvelopes]);

  // A COMPLETED envelope normally means 'done' — unless the workflow step was
  // reset, which reopens the task while the old envelope stays COMPLETED. In
  // that case the task is a redo: offer Prepare & Send again (the backend
  // allows a new envelope when none is in flight).
  const taskIsActive = ACTIVE_TASK_STATUSES.includes(task.status);

  const phase: TEsignPhase = !latestEnvelope
    ? 'select'
    : latestEnvelope.status === SignatureEnvelopeStatus.CREATED
      ? 'draft'
      : latestEnvelope.status === SignatureEnvelopeStatus.COMPLETED
        ? taskIsActive
          ? 'select'
          : 'done'
        : latestEnvelope.status === SignatureEnvelopeStatus.DECLINED ||
            latestEnvelope.status === SignatureEnvelopeStatus.VOIDED ||
            latestEnvelope.status === SignatureEnvelopeStatus.FAILED
          ? 'action_required'
          : 'in_flight';

  if (!workflowExecution || !stepDefinition) {
    return (
      <div className="p-4 text-center">
        <p className="text-text-error txt-small-regular">
          Failed to load workflow step information
        </p>
      </div>
    );
  }

  const isTaskActive = taskIsActive;
  // Choosing signers and sending is an agent/admin responsibility — clients
  // see the action disabled until the request has been sent on their behalf.
  const canSend =
    role === TaskParty.AGENT || role === TaskParty.ADMIN || canRequestSignature;

  const documentName =
    latestEnvelope?.Documents[0]?.SourceFile.fileName ?? task.title;

  const refresh = () => {
    void mutateEnvelopes();
    void mutate();
  };

  const cardLabel = () => {
    switch (phase) {
      case 'done':
        return (
          <ServiceRequestTaskLabel
            label={`${task.title} completed`}
            status={task.status}
            timestamp={task.updatedAt}
          />
        );
      case 'action_required':
        return <ServiceRequestTaskLabel label={task.title} />;
      default:
        return <ServiceRequestTaskLabel label={task.title} />;
    }
  };

  const onDiscardDraft = async () => {
    if (!latestEnvelope) return;

    const res = await discardEnvelope(latestEnvelope.id);

    if (!res.ok) {
      showNotification({
        title: 'Could not discard this draft. Please try again.',
        valid: false,
      });
      return;
    }

    refresh();
  };

  const cardActions = () => {
    if (phase === 'draft' && canSend) {
      return (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            onDiscardDraft().catch(() => undefined);
          }}
        >
          Discard draft
        </Button>
      );
    }
    if (isAdHoc && !isAdHocSendable) return null;
    if (phase === 'select' && isTaskActive && canSend) {
      return (
        <Button size="sm" onClick={() => setSendModalOpen(true)}>
          Prepare &amp; Send
        </Button>
      );
    }
    if (phase === 'action_required' && canSend) {
      return (
        <Button size="sm" onClick={() => setSendModalOpen(true)}>
          Choose Signers &amp; Resend
        </Button>
      );
    }
    return null;
  };

  const getCardDescription = (): string | undefined => {
    if (isAdHoc && !isAdHocSendable)
      return 'This signature step is not available in this environment yet.';

    switch (phase) {
      case 'select':
        return canSend
          ? 'Choose the signers and send the document for e-signature. Each signer receives an email with a link to review and sign.'
          : 'Your agent will prepare and send this document for signature — no action is needed from you yet.';
      case 'in_flight': {
        if (!latestEnvelope) return undefined;

        const total = latestEnvelope.Recipients.length;

        const pending = latestEnvelope.Recipients.filter(
          (r) =>
            r.status !== SignatureRecipientStatus.SIGNED &&
            r.status !== SignatureRecipientStatus.DECLINED
        ).length;

        return `Signature request sent — waiting for ${pending} of ${total} signature${total === 1 ? '' : 's'}. Each signer received an email with a signing link, and this task completes automatically once everyone has signed.`;
      }

      case 'action_required': {
        const reason =
          latestEnvelope?.status === SignatureEnvelopeStatus.DECLINED
            ? `${latestEnvelope.declinedByName ?? 'A signer'} declined to sign.`
            : latestEnvelope?.status === SignatureEnvelopeStatus.FAILED
              ? 'The signing email could not be delivered — the recipient address may be incorrect.'
              : 'The signature request was cancelled.';
        return canSend
          ? `${reason} Adjust the signers and send a new request — anyone who already signed will need to sign again.`
          : `${reason} Your agent will review and send an updated request.`;
      }

      case 'draft':
        return canSend
          ? 'This request was prepared but never sent — the signing window was closed before the document went out. Discard the draft to start again.'
          : 'Your agent is still preparing this signature request.';

      case 'done':
        return 'All signatures collected. The signed document is available below.';
      default:
        return undefined;
    }
  };

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: () => ({
      label: cardLabel(),
      actions: cardActions(),
      description: getCardDescription(),
    }),
  };

  const showRecipientCards =
    latestEnvelope != null &&
    (phase === 'in_flight' || phase === 'done' || phase === 'action_required');

  return (
    <div className="flex flex-col">
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      {showRecipientCards && (
        <div className="flex flex-col divide-y divide-border-secondary border border-border-secondary rounded-lg mt-2">
          {latestEnvelope.Recipients.map((recipient) => (
            <SignatureTaskCard
              key={recipient.id}
              recipient={recipient}
              documentName={documentName}
              envelopeId={latestEnvelope.id}
              envelopeStatus={latestEnvelope.status}
              documents={latestEnvelope.Documents}
              role={role}
              allRecipients={latestEnvelope.Recipients}
              voidedReason={latestEnvelope.voidedReason}
              declinedByName={latestEnvelope.declinedByName}
              onReminderSent={refresh}
              onEnvelopeCancelled={refresh}
            />
          ))}
        </div>
      )}
      {isAdHocSendable ? (
        <AdHocSignatureTaskModal
          isOpen={sendModalOpen}
          onClose={() => setSendModalOpen(false)}
          taskId={task.id}
          serviceRequest={serviceRequest}
          onSent={() => {
            setSendModalOpen(false);
            refresh();
          }}
        />
      ) : isAdHoc ? null : (
        <EsignSendModal
          isOpen={sendModalOpen}
          onClose={() => setSendModalOpen(false)}
          taskId={task.id}
          stepDefinition={stepDefinition as THumanEsignTemplateStepDefinition}
          previousEnvelope={
            phase === 'action_required' || phase === 'select'
              ? latestEnvelope
              : undefined
          }
          onSent={() => {
            setSendModalOpen(false);
            refresh();
          }}
        />
      )}
    </div>
  );
};

const EsignSendModal = ({
  isOpen,
  onClose,
  taskId,
  stepDefinition,
  previousEnvelope,
  onSent,
}: {
  isOpen: boolean;
  onClose: () => void;
  taskId: number;
  stepDefinition: THumanEsignTemplateStepDefinition;
  /** Restart flow: slots start from the last attempt's signers. */
  previousEnvelope?: SignatureEnvelope;
  onSent: () => void;
}) => {
  const [signers, setSigners] = useState<Record<string, TSignerDraft>>({});
  const [sending, setSending] = useState(false);

  const { prefill: rawPrefill, isLoading } = useEsignTaskPrefill(
    isOpen ? taskId : null
  );
  const prefill = rawPrefill?.mode === 'template' ? rawPrefill : undefined;

  // Seed slots once per open: previous attempt's signers win over context
  // suggestions; both lose to anything the user already typed.
  useEffect(() => {
    if (!isOpen || !prefill) return;

    setSigners((current) => {
      const next: Record<string, TSignerDraft> = {};
      for (const prefillRole of prefill.roles) {
        const previous = previousEnvelope?.Recipients.find(
          (r) => r.roleName === prefillRole.roleName
        );
        next[prefillRole.roleName] = current[prefillRole.roleName] ?? {
          name: previous?.recipientName ?? prefillRole.name ?? '',
          email: previous?.recipientEmail ?? prefillRole.email ?? '',
        };
      }
      return next;
    });
  }, [isOpen, prefill, previousEnvelope]);

  const roles = prefill?.roles ?? [];

  const allValid =
    roles.length > 0 &&
    roles.every((role) => {
      const draft = signers[role.roleName];
      return (
        draft != null &&
        draft.name.trim().length > 0 &&
        EMAIL_RE.test(draft.email.trim())
      );
    });

  const emails = roles
    .map((role) => signers[role.roleName]?.email.trim().toLowerCase())
    .filter((email): email is string => Boolean(email));
  const hasDuplicateSigner = new Set(emails).size !== emails.length;

  const updateSigner = (
    roleName: string,
    key: keyof TSignerDraft,
    value: string
  ) => {
    setSigners((current) => ({
      ...current,
      [roleName]: {
        ...(current[roleName] ?? { name: '', email: '' }),
        [key]: value,
      },
    }));
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await createEnvelopeFromTemplate({
        signatureTemplateId: stepDefinition.signatureTemplateId,
        serviceRequestTaskId: taskId,
        signers: roles.map((role) => ({
          roleName: role.roleName,
          name: signers[role.roleName]!.name.trim(),
          email: signers[role.roleName]!.email.trim(),
        })),
      });

      if (!res.ok) {
        showNotification({
          title:
            res.status === 409
              ? 'This task already has an active signature request'
              : 'Failed to send signature request',
          valid: false,
        });
        return;
      }

      showNotification({ title: 'Signature request sent', valid: true });
      onSent();
    } catch {
      showNotification({
        title: 'Failed to send signature request',
        valid: false,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="flex flex-col overflow-hidden max-h-[85vh]">
        <DialogHeader>
          <DialogTitle>
            Send for signature{prefill ? `: ${prefill.templateName}` : ''}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-4 px-6 overflow-y-auto">
          {previousEnvelope && (
            <Alert
              variant="warning"
              description="This sends a brand-new signature request. Anyone who already signed the previous one will need to sign again."
            />
          )}
          {isLoading && (
            <p className="txt-small-regular text-text-secondary">
              Loading signer suggestions…
            </p>
          )}
          {roles.map((role) => (
            <FieldWrapper key={role.roleName}>
              <Label>
                {role.displayName}
                {roles.some((r) => r.routingOrder !== roles[0]?.routingOrder) &&
                  ` (signs ${role.routingOrder}${role.routingOrder === 1 ? 'st' : role.routingOrder === 2 ? 'nd' : role.routingOrder === 3 ? 'rd' : 'th'})`}
              </Label>
              <div className="flex flex-col gap-2">
                <Input
                  value={signers[role.roleName]?.name ?? ''}
                  placeholder="Full name"
                  onChange={(e) =>
                    updateSigner(role.roleName, 'name', e.target.value)
                  }
                />
                <Input
                  value={signers[role.roleName]?.email ?? ''}
                  placeholder="Email address"
                  onChange={(e) =>
                    updateSigner(role.roleName, 'email', e.target.value)
                  }
                />
              </div>
            </FieldWrapper>
          ))}
          {hasDuplicateSigner && (
            <Alert
              variant="warning"
              description="Two roles share the same email address. That's allowed (e.g. a sole director in two capacities) — just make sure it's intentional."
            />
          )}
          <p className="txt-small-regular text-text-secondary">
            Each signer receives an email from DocuSign with a link to review
            and sign. The workflow continues automatically once everyone has
            signed.
          </p>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={!allValid || sending}>
            {sending ? 'Sending…' : 'Send for Signature'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
